<template>
    <main class="p-5 bg-echoes-background-black">
        <!-- Header Section -->
        <AdminHeader>
            <template #title>Cache Management</template>
            <template #description>
                View and manage cached data. Monitor cache keys, expiration times, and storage usage.
            </template>
            <template #actions>
                <button 
                    @click="refreshData"
                    :disabled="isLoading"
                    class="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-echoes-background-gray text-white hover:bg-echoes-background-gray/80 transition-colors duration-200 border border-echoes-border-light disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Refresh"
                >
                    <i class="mdi mdi-refresh text-xl" :class="{ 'animate-spin': isLoading }"></i>
                </button>
                <button 
                    v-if="selectedCaches.length > 0"
                    @click="handleDeleteSelected" 
                    class="inline-flex items-center px-4 py-2.5 text-base font-medium rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors duration-200"
                >
                    <i class="mdi mdi-delete mr-2"></i> Delete Selected ({{ selectedCaches.length }})
                </button>
            </template>
        </AdminHeader>

        <!-- Skeleton Loader -->
        <template v-if="isLoading">
            <!-- Stats Cards Skeleton -->
            <div class="mt-6 grid grid-cols-2 gap-6">
                <div class="bg-echoes-background-gray shadow-sm rounded-lg border border-echoes-border-light p-6">
                    <div class="flex items-center gap-4">
                        <div class="skeleton skeleton-icon w-12 h-12 rounded-full"></div>
                        <div class="flex-1">
                            <div class="skeleton skeleton-label h-4 w-24 mb-2"></div>
                            <div class="skeleton skeleton-text h-6 w-16"></div>
                        </div>
                    </div>
                </div>
                <div class="bg-echoes-background-gray shadow-sm rounded-lg border border-echoes-border-light p-6">
                    <div class="flex items-center gap-4">
                        <div class="skeleton skeleton-icon w-12 h-12 rounded-full"></div>
                        <div class="flex-1">
                            <div class="skeleton skeleton-label h-4 w-24 mb-2"></div>
                            <div class="skeleton skeleton-text h-6 w-16"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main Content Skeleton -->
            <div class="py-6">
                <div class="bg-echoes-background-gray shadow-sm rounded-lg border border-echoes-border-light">
                    <!-- Search and Filters Skeleton -->
                    <div class="p-4">
                        <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                            <div class="w-full sm:w-96">
                                <div class="skeleton skeleton-input h-11"></div>
                            </div>
                            <div class="w-full sm:w-48">
                                <div class="skeleton skeleton-input h-11"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Table Skeleton -->
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-echoes-border-light">
                            <thead class="bg-echoes-border-light">
                                <tr>
                                    <th scope="col" class="px-6 py-4 text-left">
                                        <div class="skeleton h-4 w-4"></div>
                                    </th>
                                    <th scope="col" class="px-6 py-4 text-left">
                                        <div class="skeleton h-4 w-24"></div>
                                    </th>
                                    <th scope="col" class="px-6 py-4 text-left">
                                        <div class="skeleton h-4 w-20"></div>
                                    </th>
                                    <th scope="col" class="px-6 py-4 text-left">
                                        <div class="skeleton h-4 w-16"></div>
                                    </th>
                                    <th scope="col" class="px-6 py-4 text-center">
                                        <div class="skeleton h-4 w-16 mx-auto"></div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                                <tr v-for="i in itemsPerPage" :key="`skeleton-${i}`" class="bg-echoes-background-black/80">
                                    <td class="px-6 py-4">
                                        <div class="skeleton h-4 w-4"></div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="skeleton h-4 w-48"></div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="skeleton h-4 w-32"></div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="skeleton h-4 w-20"></div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="skeleton h-9 w-20 mx-auto"></div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination Footer Skeleton -->
                    <div class="bg-echoes-background-gray border-t rounded-b-lg border-echoes-border-light px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="skeleton h-4 w-48"></div>
                            <div class="flex items-center space-x-2">
                                <div class="skeleton h-8 w-20"></div>
                                <div class="skeleton h-8 w-8"></div>
                                <div class="skeleton h-8 w-8"></div>
                                <div class="skeleton h-8 w-8"></div>
                                <div class="skeleton h-8 w-20"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- Actual Content -->
        <template v-else>
            <!-- Stats Cards -->
            <div class="mt-6 grid grid-cols-2 gap-6">
                <StatCard>
                    <template #icon>
                        <i class="mdi mdi-database text-2xl text-blue-500"></i>
                    </template>
                    <template #title>Total Caches</template>
                    <template #value>{{ totalCaches }}</template>
                </StatCard>
                
                <StatCard>
                    <template #icon>
                        <i class="mdi mdi-harddisk text-2xl text-green-500"></i>
                    </template>
                    <template #title>Total Cache Size</template>
                    <template #value>{{ totalCacheSize }}</template>
                </StatCard>
            </div>

            <!-- Main Content -->
            <div class="py-6">
                <div class="bg-echoes-background-gray shadow-sm rounded-lg border border-echoes-border-light">
                    <!-- Search and Filters -->
                    <div class="p-4">
                        <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                            <div class="w-full sm:w-96">
                                <TextInput 
                                    v-model="searchQuery" 
                                    placeholder="Search by cache key..." 
                                    class="w-full"
                                />
                            </div>
                            <div class="flex items-center gap-4">
                                <div class="w-full sm:w-48">
                                    <SelectInput
                                        v-model="itemsPerPage"
                                        :options="itemsPerPageOptions"
                                        :hide-search="true"
                                        @update:modelValue="currentPage = 1"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Table -->
                    <div>
                        <div v-if="filteredCaches.length === 0" class="h-[40vh]">
                            <div class="flex items-center justify-center h-full">
                                <div class="text-white/80 text-sm">No caches found</div>
                            </div>
                        </div>
                        <table v-else class="min-w-full divide-y divide-echoes-border-light">
                            <thead class="bg-echoes-border-light">
                                <tr>
                                    <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                        <Checkbox 
                                            :checked="allSelected"
                                            @update:checked="toggleSelectAllFromEvent"
                                        />
                                    </th>
                                    <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                        Cache Key
                                    </th>
                                    <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                        Expiration
                                    </th>
                                    <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                        Size
                                    </th>
                                    <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-white uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                                <template v-for="cache in paginatedCaches" :key="cache.key">
                                    <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-200">
                                        <td class="px-6 py-4">
                                            <Checkbox 
                                                :checked="selectedCaches.includes(cache.key)"
                                                @update:checked="(checked) => toggleCacheSelection(cache.key, checked)"
                                            />
                                        </td>
                                        <td class="px-6 py-4">
                                            <div class="flex items-center">
                                                <div class="text-sm font-medium text-white/90 break-all">
                                                    {{ cache.key }}
                                                </div>
                                            </div>
                                        </td>
                                        <td class="px-6 py-4">
                                            <div class="text-sm text-white/80">
                                                {{ formatExpiration(cache) }}
                                            </div>
                                        </td>
                                        <td class="px-6 py-4">
                                            <div class="text-sm font-medium text-white/90">
                                                {{ formatSize(cache.size) }}
                                            </div>
                                        </td>
                                        <td class="px-6 py-4">
                                            <div class="flex items-center justify-center space-x-4">
                                                <button 
                                                    @click="deleteCache(cache.key)"
                                                    class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-red-400 hover:bg-red-600/20 transition-colors duration-200 border border-red-600/30"
                                                    title="Delete Cache"
                                                >
                                                    <i class="mdi mdi-delete-outline mr-2"></i>
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                        <!-- Pagination Footer -->
                        <div class="bg-echoes-background-gray border-t rounded-b-lg border-echoes-border-light px-6 py-4">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center text-sm text-white/80">
                                    <span>Showing</span>
                                    <span class="font-medium mx-1">
                                        {{ ((currentPage - 1) * itemsPerPage) + 1 }}
                                    </span>
                                    <span>to</span>
                                    <span class="font-medium mx-1">
                                        {{ Math.min(currentPage * itemsPerPage, filteredCaches.length) }}
                                    </span>
                                    <span>of</span>
                                    <span class="font-medium ml-1">{{ filteredCaches.length }}</span>
                                </div>
                                <div class="flex items-center space-x-2">
                                    <button
                                        @click="currentPage = Math.max(1, currentPage - 1)"
                                        :disabled="currentPage === 1"
                                        class="px-3 py-1 rounded-md text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                   
                                    <div class="flex items-center space-x-1">
                                        <template v-for="(item, index) in visiblePages" :key="index">
                                            <template v-if="typeof item === 'number'">
                                                <button
                                                    @click="currentPage = item"
                                                    :class="[
                                                        'px-3 py-1 rounded-md text-sm font-medium',
                                                        currentPage === item
                                                            ? 'bg-echoes-primary text-white'
                                                            : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                                    ]"
                                                >
                                                    {{ item }}
                                                </button>
                                            </template>
                                            <template v-else>
                                                <span class="px-3 py-1 text-sm text-white/60">...</span>
                                            </template>
                                        </template>
                                    </div>
                                    <button
                                        @click="currentPage = Math.min(totalPages, currentPage + 1)"
                                        :disabled="currentPage === totalPages"
                                        class="px-3 py-1 rounded-md text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </main>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import AdminHeader from '../AdminHeader.vue';
import StatCard from '../StatCard.vue';
import Checkbox from '@/components/checkbox.vue';
import { useLayoutComponents } from '@/composables/layout';
import { useToast } from '@/composables/use-toast';
import { getCaches, getCacheStats, deleteCache as deleteCacheApi } from './cacheService';

const { TextInput, SelectInput } = useLayoutComponents();
const { addToast } = useToast();

const caches = ref([]);
const stats = ref({ 
    totalKeys: 0, 
    totalSize: 0, 
    keysWithExpiration: 0, 
    keysWithoutExpiration: 0, 
    averageSize: 0 
});
const isLoading = ref(false);

const loadCaches = async () => {
    try {
        isLoading.value = true;
        const response = await getCaches();
        caches.value = response;
    } catch (error) {
        addToast('Failed to load caches', 'error');
        console.error('Error loading caches:', error);
    } finally {
        isLoading.value = false;
    }
};

const loadStats = async () => {
    try {
        const response = await getCacheStats();
        stats.value = response;
    } catch (error) {
        addToast('Failed to load cache statistics', 'error');
        console.error('Error loading cache stats:', error);
    }
};

const refreshData = async () => {
    await Promise.all([loadCaches(), loadStats()]);
    addToast('Cache data refreshed', 'success');
};

const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(12);
const selectedCaches = ref([]);

const itemsPerPageOptions = [
    { value: 6, label: '6' },
    { value: 12, label: '12' },
    { value: 24, label: '24' },
    { value: 48, label: '48' },
];

const filteredCaches = computed(() => {
    if (!searchQuery.value) {
        return caches.value;
    }
    
    const query = searchQuery.value.toLowerCase();
    return caches.value.filter(cache => 
        cache.key.toLowerCase().includes(query)
    );
});

const paginatedCaches = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredCaches.value.slice(start, end);
});

const totalPages = computed(() => {
    return Math.ceil(filteredCaches.value.length / itemsPerPage.value);
});

const totalCaches = computed(() => {
    return stats.value.totalKeys || caches.value.length;
});

const totalCacheSize = computed(() => {
    if (stats.value.totalSize) {
        return formatSize(stats.value.totalSize);
    }
    const totalBytes = caches.value.reduce((sum, cache) => sum + cache.size, 0);
    return formatSize(totalBytes);
});

const allSelected = computed(() => {
    return paginatedCaches.value.length > 0 && 
           paginatedCaches.value.every(cache => selectedCaches.value.includes(cache.key));
});

const addPageRange = (pages, start, end) => {
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }
};

const buildPagesForStart = (pages, total) => {
    addPageRange(pages, 1, 4);
    pages.push('...', total);
};

const buildPagesForEnd = (pages, total) => {
    pages.push(1, '...');
    addPageRange(pages, total - 3, total);
};

const buildPagesForMiddle = (pages, current, total) => {
    pages.push(1, '...');
    addPageRange(pages, current - 1, current + 1);
    pages.push('...', total);
};

const visiblePages = computed(() => {
    const pages = [];
    const total = totalPages.value;
    const current = currentPage.value;
    
    if (total <= 7) {
        addPageRange(pages, 1, total);
        return pages;
    }
    
    if (current <= 3) {
        buildPagesForStart(pages, total);
    } else if (current >= total - 2) {
        buildPagesForEnd(pages, total);
    } else {
        buildPagesForMiddle(pages, current, total);
    }
    
    return pages;
});

const formatSize = (bytes) => {
    if (bytes < 1024) {
        return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(2)} KB`;
    } else {
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
};

const formatExpiration = (cache) => {
    if (cache.ttl === -1 || !cache.expiresAt) {
        return 'Permanent';
    }
    
    const expDate = new Date(cache.expiresAt);
    return expDate.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
};

const toggleSelectAllFromEvent = (checked) => {
    if (checked) {
        paginatedCaches.value.forEach(cache => {
            if (!selectedCaches.value.includes(cache.key)) {
                selectedCaches.value.push(cache.key);
            }
        });
    } else {
        paginatedCaches.value.forEach(cache => {
            const index = selectedCaches.value.indexOf(cache.key);
            if (index > -1) {
                selectedCaches.value.splice(index, 1);
            }
        });
    }
};

const toggleCacheSelection = (cacheKey, checked) => {
    if (checked) {
        if (!selectedCaches.value.includes(cacheKey)) {
            selectedCaches.value.push(cacheKey);
        }
    } else {
        const index = selectedCaches.value.indexOf(cacheKey);
        if (index > -1) {
            selectedCaches.value.splice(index, 1);
        }
    }
};

const deleteCache = async (cacheKey) => {
    try {
        await deleteCacheApi(cacheKey);
        
        const index = caches.value.findIndex(c => c.key === cacheKey);
        if (index > -1) {
            caches.value.splice(index, 1);
        }
        
        const selectedIndex = selectedCaches.value.indexOf(cacheKey);
        if (selectedIndex > -1) {
            selectedCaches.value.splice(selectedIndex, 1);
        }
        
        await loadStats();
        addToast('Cache deleted successfully', 'success');
    } catch (error) {
        addToast(`Failed to delete cache: ${error.response?.data?.message || error.message}`, 'error');
        console.error('Error deleting cache:', error);
    }
};

const handleDeleteSelected = async () => {
    if (selectedCaches.value.length === 0) {
        addToast('No caches selected', 'error');
        return;
    }
    
    const cacheKeysToDelete = [...selectedCaches.value];
    let successCount = 0;
    let errorCount = 0;
    
    try {
        for (const cacheKey of cacheKeysToDelete) {
            try {
                await deleteCacheApi(cacheKey);
                
                const index = caches.value.findIndex(c => c.key === cacheKey);
                if (index > -1) {
                    caches.value.splice(index, 1);
                }
                
                successCount++;
            } catch (error) {
                errorCount++;
                console.error(`Error deleting cache ${cacheKey}:`, error);
            }
        }
        
        await loadStats();
        selectedCaches.value = [];
        
        if (errorCount === 0) {
            addToast(`${successCount} cache(s) deleted successfully`, 'success');
        } else if (successCount > 0) {
            addToast(`${successCount} cache(s) deleted, ${errorCount} failed`, 'warning');
        } else {
            addToast('Failed to delete selected caches', 'error');
        }
    } catch (error) {
        addToast('An error occurred while deleting caches', 'error');
        console.error('Error in handleDeleteSelected:', error);
    }
};

onMounted(() => {
    loadCaches();
    loadStats();
});
</script>

<style scoped>
.skeleton {
    @apply bg-echoes-background-gray rounded-[13px];
    background: linear-gradient(90deg, 
        rgba(255, 255, 255, 0.05) 0%, 
        rgba(255, 255, 255, 0.1) 50%, 
        rgba(255, 255, 255, 0.05) 100%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s ease-in-out infinite;
}

@keyframes skeleton-loading {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

.skeleton-icon {
    @apply w-12 h-12;
}

.skeleton-label {
    @apply h-4;
}

.skeleton-text {
    @apply h-6;
}

.skeleton-input {
    @apply h-11 w-full;
}
</style>

